/**
 * T-STACKER 2.0 - Game Constants
 * 
 * Central configuration for the Rapier + Canvas implementation.
 * All values are tuned for mobile-first, touch-based gameplay.
 */

// ============================================================================
// PACKAGE SIZING
// ============================================================================

/**
 * Global size constraints for all packages.
 * Ensures packages are grabbable on mobile and fit on conveyor.
 */
export const SIZE_CONSTRAINTS = {
    /** Minimum dimension for any package (ensures finger-friendly grabbing) */
    minGrabSize: 44,
    /** Maximum dimension as percentage of container width (ensures conveyor fit) */
    maxSizePercent: 0.40,
} as const;

/**
 * Box size classes with spawn weights.
 * Medium: 50%, Small: 35%, Large: 15%
 * Areas are in pixels squared at reference width.
 */
export const BOX_SIZE_CLASSES = {
    small: {
        weight: 0.35,
        minArea: 1800,
        maxArea: 4000,
    },
    medium: {
        weight: 0.50,
        minArea: 4000,
        maxArea: 9000,
    },
    large: {
        weight: 0.15,
        minArea: 9000,
        maxArea: 16000,
    },
} as const;

/**
 * Box aspect ratio configuration.
 * Uses logarithmic distribution for equal probability of wide vs tall.
 */
export const BOX_ASPECT_RATIO = {
    /** Minimum aspect ratio (width/height) - wide boxes */
    min: 0.25,
    /** Maximum aspect ratio (width/height) - tall boxes */
    max: 4.0,
} as const;

/**
 * Bag sizing configuration.
 * Uses simple linear random ranges.
 */
export const BAG_SIZE = {
    /** Width range in pixels (at reference width) */
    width: { min: 75, max: 105 },
    /** Height range in pixels (at reference width) */
    height: { min: 60, max: 90 },
    /** Chamfer radius as percentage of smaller dimension */
    chamferRatio: 0.15,
} as const;

/**
 * Triangle sizing configuration.
 */
export const TRIANGLE_SIZE = {
    /** Radius range in pixels (at reference width) */
    radius: { min: 45, max: 75 },
} as const;

/**
 * Mass scaling configuration.
 * Larger packages feel heavier.
 */
export const MASS_SCALING = {
    /** Reference area for base density (packages this size have density 1.0) */
    referenceArea: 5000,
    /** Minimum density multiplier (for smallest packages) */
    minDensity: 0.6,
    /** Maximum density multiplier (for largest packages) */
    maxDensity: 1.8,
} as const;

// ============================================================================
// SPAWN RATES
// ============================================================================

/**
 * Package spawn rate distribution.
 * Values are cumulative thresholds for random selection.
 */
export const SPAWN_RATES = {
    /** Probability of spawning a box (0-1) */
    box: 0.85,
    /** Probability of spawning a bag (cumulative with box) */
    bag: 0.10,
    /** Probability of spawning a triangle (remainder) */
    triangle: 0.05,
} as const;

// ============================================================================
// PHYSICS PROPERTIES
// ============================================================================

/**
 * Physics properties per package type.
 * These affect stacking behavior and feel.
 * 
 * Note: linearDamping at 0 gives natural falling/throwing physics.
 * Density increased for heavier, more satisfying feel.
 * Friction kept high for stable stacking.
 */
export const PACKAGE_PHYSICS = {
    box: {
        friction: 1.0,
        density: 2.0,        // Increased for heavier feel
        restitution: 0,
        linearDamping: 0,    // No air resistance for natural physics
    },
    bag: {
        friction: 1.0,
        density: 1.6,        // Slightly lighter than boxes
        restitution: 0,
        linearDamping: 0,
    },
    triangle: {
        friction: 0.95,
        density: 1.6,
        restitution: 0.05,
        linearDamping: 0,
    },
} as const;

// ============================================================================
// RESPONSIVE LAYOUT
// ============================================================================

/**
 * Reference width for scaling calculations.
 * Package sizes are defined relative to this width.
 */
export const REFERENCE_WIDTH = 600;

/**
 * Container layout as percentages of screen dimensions.
 */
export const CONTAINER_LAYOUT = {
    /** Container width as percentage of screen width */
    widthPercent: 0.95,
    /** Container height as percentage of screen height */
    heightPercent: 0.60,
    /** Wall thickness as percentage of screen width */
    wallThicknessPercent: 0.025,
    /** Floor offset from bottom as percentage of screen height */
    floorOffsetPercent: 0.05,
} as const;

/**
 * Conveyor layout configuration.
 * All values are now percentages for responsive scaling.
 */
export const CONVEYOR_LAYOUT = {
    /** Conveyor Y position as percentage from top */
    yPercent: 0.25,
    /** Conveyor width as percentage of screen width */
    widthPercent: 1.0,
    /** Conveyor height as percentage of viewport height */
    heightPercent: 0.025,
    /** Minimum conveyor height in pixels (for very small screens) */
    minHeight: 16,
    /** Off-screen padding as percentage of viewport width */
    offScreenPaddingPercent: 0.25,
    /** Spawn X position as percentage (negative = off-screen left) */
    spawnXPercent: -0.08,
} as const;

// ============================================================================
// DIFFICULTY SETTINGS
// ============================================================================

export interface DifficultySettings {
    /** Belt speed multiplier */
    beltSpeed: number;
    /** Spawn interval in milliseconds */
    spawnInterval: number;
    /** Fill threshold to trigger finishing phase (0-1) */
    fillThreshold: number;
    /** Duration of finishing phase in seconds */
    finishingDuration: number;
    /** T-Junction bonus points */
    tJunctionBonus: number;
}

export const DIFFICULTIES: Record<'easy' | 'normal' | 'hard', DifficultySettings> = {
    easy: {
        beltSpeed: 1.0,
        spawnInterval: 4000,
        fillThreshold: 0.7,
        finishingDuration: 10,
        tJunctionBonus: 500,
    },
    normal: {
        beltSpeed: 1.5,
        spawnInterval: 2500,
        fillThreshold: 0.7,
        finishingDuration: 10,
        tJunctionBonus: 500,
    },
    hard: {
        beltSpeed: 2.2,
        spawnInterval: 1800,
        fillThreshold: 0.7,
        finishingDuration: 10,
        tJunctionBonus: 750,
    },
};

// ============================================================================
// TIMING
// ============================================================================

export const TIMING = {
    /** Time for physics to settle after finishing phase (ms) */
    settleTime: 3000,
    /** Buffer gap between package and conveyor surface as percentage of viewport height */
    spawnBufferPercent: 0.02,
} as const;

// ============================================================================
// SCORING
// ============================================================================

export const SCORING = {
    /** Percentile for outlier detection in density calculation */
    outlierPercentile: 0.05,
    /** Minimum velocity for a body to be considered "settled" */
    settledVelocityThreshold: 0.5,
    /** Grid cell size as percentage of container width for density sampling */
    gridCellSizePercent: 0.033,
} as const;

// ============================================================================
// INPUT
// ============================================================================

export const INPUT = {
    /** Spring stiffness for held body following */
    springStiffness: 300,
    /** Spring damping for held body following */
    springDamping: 35,
    /** Rotation sensitivity for arc rotation */
    rotationSensitivity: 0.008,
    /** Minimum tangent speed to trigger rotation as percentage of viewport width per frame */
    rotationMinTangentSpeedPercent: 0.005,
    /** Distance threshold for rotation warmup as percentage of viewport width */
    rotationWarmupDistancePercent: 0.04,
    /** Time threshold for rotation warmup (ms) */
    rotationWarmupTime: 100,
    /** Maximum throw speed as percentage of viewport width per second */
    maxThrowSpeedPercent: 3.0,
    /** Keyboard rotation step (radians) - 45 degrees */
    keyboardRotationStep: Math.PI / 4,
} as const;

// ============================================================================
// CONVEYOR PHYSICS
// ============================================================================

/**
 * Physics parameters for conveyor belt stabilization.
 * Uses torque-based correction to keep packages upright without fighting physics.
 */
export const CONVEYOR_PHYSICS = {
    /** Angular damping while on belt (high = resists rotation strongly) */
    beltAngularDamping: 15.0,
    /** Rotation correction strength (spring stiffness for angle correction) */
    rotationCorrectionStrength: 8.0,
    /** Maximum correction torque to prevent over-correction */
    maxCorrectionTorque: 5.0,
    /** Default angular damping for packages (applied when leaving belt) */
    defaultAngularDamping: 1.0,
    /** Detection height above belt as percentage of viewport height */
    detectionHeightPercent: 0.10,
    /** Resting velocity threshold as percentage of viewport height per second */
    restingThresholdPercent: 0.06,
} as const;

// ============================================================================
// RENDERER SETTINGS
// ============================================================================

/**
 * Renderer visual settings - all percentages for responsive scaling.
 */
export const RENDERER = {
    /** Chevron spacing as percentage of conveyor width */
    chevronSpacingPercent: 0.05,
    /** Chevron size as percentage of conveyor height */
    chevronSizePercent: 0.4,
    /** Line width scale factor (multiplied by DPR internally) */
    baseLineWidth: 1,
} as const;

// ============================================================================
// VISUAL COLORS
// ============================================================================

export const COLORS = {
    background: '#1a1a1a',
    box: '#8B4513',
    boxBorder: '#5D3A1A',
    bag: '#C19A6B',
    bagBorder: '#8B7355',
    bagDecoration: '#6B4423',
    triangle: '#CD853F',
    triangleBorder: '#8B5A2B',
    wall: '#555555',
    conveyor: '#333333',
    conveyorPattern: '#444444',
    highlight: '#FFFF00',
    fillLine: '#666666',
    springLine: '#FFFF00',
} as const;
